import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseCellComponent} from '../_base-cell/base-cell.component';
import {SortTableCellCurrentValue} from '../../../../../shared/models/sort-table/cell-classes';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {Subscription} from 'rxjs';
import {Tile} from '../../../../../shared/models/tile';

import {TilesService} from '../../../../../core/services/tiles.service';
import {TilePollingService, PollConsumerId} from '../../../../../core/services/tile-polling.service';
import {TileValueParser} from '../../../../../shared/models/tile-value-parser';
import moment from 'moment';
import {SortTableCellEmptyComponent} from '../empty/sort-table-cell-empty.component';
import {calculateDataAge} from '../../../../../shared/utils/data-age.util';
import {DEFAULT_STALE_THRESHOLD_MINUTES} from '../../../../../shared/constants/tile-config.constants';
import {SpinnerComponent} from '../../../spinner/spinner.component';

@Component({
  selector: 'motn-sort-table-cell-current-value',
  template: `
    @if (isLoading) {
      <div class="current-value-cell loading">
        <motn-spinner></motn-spinner>
      </div>
    } @else if (shouldShowValue) {
      <div class="current-value-cell">
        <i class="fa-regular fa-line-chart chart-icon"></i>
        <span class="value" [class.outdated]="isOutdated">{{ displayValue }} {{ displayUnits }}</span>
        @if (staleValue) {
          <span class="stale-value">{{ staleValue }}</span>
        }
      </div>
    } @else {
      <motn-sort-table-cell-empty></motn-sort-table-cell-empty>
    }
  `,
  styleUrls: ['./sort-table-cell-current-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SortTableCellEmptyComponent,
    SpinnerComponent
],
})
export class SortTableCellCurrentValueComponent extends BaseCellComponent implements OnInit, OnChanges, OnDestroy {
  @Input() cell: SortTableCellCurrentValue;
  @Input() isVisible: boolean = false;

  tile: Tile;
  displayValue: string = '';
  displayUnits: string = '';
  isOutdated: boolean = false;
  staleValue: string = '';
  shouldShowValue: boolean = false;
  isLoading: boolean = false;

  private tileSub: Subscription;
  private tileValueSub: Subscription;
  private isPolling: boolean = false;
  private initialFetchTimeout: any;
  /** Stable ID that uniquely identifies this component instance in the polling service. */
  private readonly pollConsumerId: PollConsumerId = crypto.randomUUID();

  constructor(
    private store: Store<AppState>,
    private tilesService: TilesService,
    private tilePollingService: TilePollingService,
  ) {
    super();
  }

  protected onInit(): void {
    // Subscribe to tile updates from the store
    this.tileSub = this.store.select('tiles').subscribe(
      (state) => {
        this.tile = state.all[this.cell.tileId];
        if (this.tile) {
          this.subscribeToTileValues();
        } else {
          this.stopPolling();
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When visibility changes, start or stop polling
    if (changes['isVisible'] && this.tile) {
      if (this.isVisible) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.tileSub?.unsubscribe();
    this.tileValueSub?.unsubscribe();

    // Clear timeout on component destruction
    if (this.initialFetchTimeout) {
      clearTimeout(this.initialFetchTimeout);
      this.initialFetchTimeout = null;
    }
  }

  private subscribeToTileValues(): void {
    // Only subscribe once
    if (this.tileValueSub) {
      return;
    }

    // Subscribe to tile value updates
    this.tileValueSub = this.tilesService.tileValues$.subscribe(
      (tileValues) => {
        const tileValue = tileValues[this.cell.tileId];

        if (tileValue) {
          // Clear the initial fetch timeout if it exists
          if (this.initialFetchTimeout) {
            clearTimeout(this.initialFetchTimeout);
            this.initialFetchTimeout = null;
          }

          // Stop loading spinner when we get a value for THIS tile
          this.isLoading = false;
          this.updateDisplayValue(tileValue);
        }
      },
    );

    // Start polling if the row is visible
    if (this.isVisible) {
      this.startPolling();
    }
  }

  /**
   * Starts polling for tile values when the row becomes visible.
   * Uses TilePollingService for continuous updates, same as dashboard tiles.
   */
  private startPolling(): void {
    if (!this.tile || this.isPolling || !this.tile.typeSupportsPolling()) {
      return;
    }

    this.isLoading = true;
    this.tilePollingService.startPolling(this.pollConsumerId, this.tile);
    this.isPolling = true;

    // Set a timeout to turn off the spinner if no value arrives after initial fetch
    // Empty array responses mean there's no value, so we should show empty state instead of spinner
    this.initialFetchTimeout = setTimeout(() => {
      if (this.isLoading && this.isPolling) {
        this.isLoading = false;
      }
      this.initialFetchTimeout = null;
    }, 2000); // 2 seconds should be enough for the first fetch to complete
  }

  /**
   * Stops polling for tile values when the row is no longer visible.
   */
  private stopPolling(): void {
    if (this.isPolling) {
      this.tilePollingService.stopPolling(this.pollConsumerId, this.cell.tileId);
      this.isPolling = false;
      this.isLoading = false;

      if (this.initialFetchTimeout) {
        clearTimeout(this.initialFetchTimeout);
        this.initialFetchTimeout = null;
      }
    }
  }

  private updateDisplayValue(tileValue: any): void {
    // If no tile value data, don't show anything
    if (!this.tile || !tileValue || tileValue.Value === null || tileValue.Value === undefined) {
      this.displayValue = '';
      this.displayUnits = '';
      this.isOutdated = false;
      this.shouldShowValue = false;
      this.cell.hasValue = false;
      return;
    }

    // Parse and format the value using TileValueParser
    const parser = new TileValueParser(this.tile, tileValue.Value);
    this.displayValue = parser.getDisplayValue();
    this.displayUnits = this.tile.getUnitsLabel() || '';

    // Only show the cell content if there's actually a display value
    this.shouldShowValue = !!this.displayValue;
    this.cell.hasValue = this.shouldShowValue;

    // Check if value is outdated and calculate stale value
    if (tileValue.Time) {
      const valueMoment = moment.utc(tileValue.Time);
      const ageInSeconds = moment().diff(valueMoment, 'seconds');

      // Use same logic as dashboard tiles: show age for data 10min or older
      const result = calculateDataAge(ageInSeconds, { minAgeMinutes: DEFAULT_STALE_THRESHOLD_MINUTES });
      this.isOutdated = result.isStale;
      this.staleValue = result.dataAge;
    } else {
      this.isOutdated = false;
      this.staleValue = '';
    }
  }
}
